import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { ExternalLink, Loader2, Brain, Zap, Users } from "lucide-react";
import axios from "axios";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "all", label: "All", icon: null },
  { key: "neurodiversity", label: "Neurodiversity", icon: Brain },
  { key: "adhd", label: "ADHD", icon: Zap },
  { key: "community", label: "Community", icon: Users },
];

function timeAgo(dateStr) {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const days = Math.floor((now - date) / 86400000);
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return `${Math.floor(days / 30)} months ago`;
  } catch {
    return "";
  }
}

function stripHtml(str) {
  return str.replace(/&[^;]+;/g, " ").replace(/\s+/g, " ").trim();
}

export default function ResourcesPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "all";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/resources`, {
          params: { category: activeCategory },
        });
        setArticles(res.data.articles || []);
      } catch {
        setArticles([]);
      }
      setLoading(false);
    };
    fetchArticles();
  }, [activeCategory]);

  const setCategory = (cat) => {
    if (cat === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ category: cat });
    }
  };

  // Group by source
  const grouped = {};
  articles.forEach((a) => {
    if (!grouped[a.source]) grouped[a.source] = [];
    grouped[a.source].push(a);
  });

  return (
    <div data-testid="resources-page" className="min-h-screen bg-[#F8F9FB]">
      <SEO
        title="Resources & Reading"
        description="Curated articles from trusted voices in the neurodiversity, ADHD, and SEND communities. Updated daily with the latest news, research, and lived experience."
        path="/resources"
      />
      <Navbar />

      <main className="pt-28 pb-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-widest text-brand-purple font-semibold mb-4">
              Stay informed
            </p>
            <h1
              data-testid="resources-title"
              className="font-heading text-3xl md:text-5xl font-bold text-[#2F3A56] leading-tight mb-4"
            >
              Resources & Reading
            </h1>
            <p className="text-base md:text-lg text-[#5A6376] max-w-2xl mx-auto leading-relaxed">
              Curated articles from trusted voices in the neurodiversity,
              ADHD, and SEND communities. Updated daily.
            </p>
          </div>

          {/* Category filters */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.key}
                data-testid={`filter-${cat.key}`}
                onClick={() => setCategory(cat.key)}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat.key
                    ? "bg-brand-teal text-white shadow-md shadow-brand-teal/20"
                    : "bg-white text-[#5A6376] border border-[#E7E9EC] hover:border-brand-teal/30 hover:text-brand-teal"
                }`}
              >
                {cat.icon && <cat.icon size={15} strokeWidth={1.5} />}
                {cat.label}
              </button>
            ))}
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2
                size={28}
                className="animate-spin text-brand-teal"
              />
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#8B95A5]">
                No articles available right now. Check back soon.
              </p>
            </div>
          ) : (
            <div className="space-y-12">
              {Object.entries(grouped).map(([source, items]) => (
                <div key={source}>
                  <h2 className="font-heading text-lg font-semibold text-[#2F3A56] mb-4 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-brand-teal" />
                    {source}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {items.map((article, i) => (
                      <a
                        key={`${source}-${i}`}
                        href={article.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`article-card-${source.toLowerCase().replace(/\s+/g, "-")}-${i}`}
                        className="group bg-white rounded-2xl border border-[#E7E9EC] p-6 shadow-[0_2px_12px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.07)] hover:-translate-y-0.5 transition-all duration-300 flex flex-col"
                      >
                        <span className="inline-block text-[10px] uppercase tracking-widest text-brand-purple font-semibold mb-3">
                          {article.category}
                        </span>
                        <h3 className="font-heading text-base font-semibold text-[#2F3A56] mb-2 leading-snug group-hover:text-brand-teal transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        {article.summary && (
                          <p className="text-xs text-[#5A6376] leading-relaxed mb-4 line-clamp-3 flex-1">
                            {stripHtml(article.summary)}
                          </p>
                        )}
                        <div className="flex items-center justify-between mt-auto pt-3 border-t border-[#E7E9EC]">
                          <span className="text-[10px] text-[#8B95A5]">
                            {timeAgo(article.published)}
                          </span>
                          <span className="inline-flex items-center gap-1 text-[10px] text-brand-teal font-medium group-hover:gap-1.5 transition-all">
                            Read <ExternalLink size={10} />
                          </span>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
